import React from 'react';
import { Page, Text, View, StyleSheet, Document, Svg, Path } from '@react-pdf/renderer';
import { registerFonts } from './fontRegistry';
import { formatProjectMetadata, formatContactLink } from '../../../utils/resumeExport';

registerFonts();

const breakableUrlStr = (s) => {
  if (!s) return '';
  return String(s).replace(/([/\-_.\?&=@])/g, '$1\u200B');
};

const cleanStr = (s) => {
  if (s === null || s === undefined) return '';
  if (typeof s === 'string') return s.trim();
  if (typeof s === 'number' || typeof s === 'boolean') return String(s);
  if (typeof s === 'object') {
    const extracted = s.text || s.description || s.label || s.title || s.name || s.value || s.language || s.proficiency || s.company || s.role || s.degree || s.project || '';
    return typeof extracted === 'string' ? extracted.trim() : '';
  }
  return '';
};

const extractRespBullets = (p) => {
  if (!p || !p.responsibilities) return [];
  if (Array.isArray(p.responsibilities)) {
    return p.responsibilities
      .map(r => (typeof r === 'string' ? r : r?.text || ''))
      .filter(t => t && t.trim());
  }
  if (typeof p.responsibilities === 'string') {
    return p.responsibilities
      .split('\n')
      .map(t => t.trim())
      .filter(Boolean);
  }
  return [];
};

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IconEmail = ({ color }) => (
  <Svg viewBox="0 0 24 24" width={10} height={10}>
    <Path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z" fill={color} />
  </Svg>
);
const IconPhone = ({ color }) => (
  <Svg viewBox="0 0 24 24" width={10} height={10}>
    <Path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill={color} />
  </Svg>
);
const IconLocation = ({ color }) => (
  <Svg viewBox="0 0 24 24" width={10} height={10}>
    <Path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={color} />
  </Svg>
);
const IconLinkedIn = ({ color }) => (
  <Svg viewBox="0 0 24 24" width={10} height={10}>
    <Path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" fill={color} />
  </Svg>
);
const IconGitHub = ({ color }) => (
  <Svg viewBox="0 0 24 24" width={10} height={10}>
    <Path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.45-1.15-1.11-1.46-1.11-1.46-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z" fill={color} />
  </Svg>
);
const IconLink = ({ color }) => (
  <Svg viewBox="0 0 24 24" width={10} height={10}>
    <Path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" fill={color} />
  </Svg>
);

const createStyles = (primaryColor = '#0f172a') => StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Roboto',
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 30,
  },
  header: {
    backgroundColor: primaryColor,
    padding: 24,
    paddingTop: 30,
    marginTop: -28,
    marginHorizontal: -30,
    marginBottom: 18,
    color: '#ffffff',
  },
  name: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 3,
    fontFamily: 'Roboto',
  },
  headline: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#e2e8f0', // Light gray/blue
    marginBottom: 4,
  },
  targetHeadline: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: '#cbd5e1',
    marginBottom: 10,
  },
  contactBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginRight: 16,
    marginBottom: 6,
  },
  contactItem: {
    fontSize: 9.5,
    color: '#f8fafc',
  },
  contactIconWrapper: {
    marginTop: 1,
  },
  body: {
    padding: 0,
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 12.5,
    fontWeight: 'bold',
    color: primaryColor,
    textTransform: 'uppercase',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 3,
    marginBottom: 10,
  },
  text: {
    fontSize: 10,
    color: '#334155',
    lineHeight: 1.5,
  },
  // Dots for skills
  skRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  skLabel: {
    fontSize: 10,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#1e293b',
  },
  skText: {
    fontSize: 10,
    fontFamily: 'Inter',
    color: '#475569',
    lineHeight: 1.5,
  },
  entryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  entryTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#0f172a',
    width: '100%',
    lineHeight: 1.25,
  },
  entryDate: {
    fontSize: 9.5,
    color: primaryColor,
    fontWeight: 'bold',
    flexShrink: 0,
  },
  entrySub: {
    fontSize: 9.5,
    color: '#475569',
    marginBottom: 6,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'flex-start',
  },
  bulletDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: primaryColor,
    marginTop: 5,
    marginRight: 8,
    marginLeft: 2,
    flexShrink: 0,
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    color: '#334155',
    lineHeight: 1.5,
  },
  entryBlock: {
    marginBottom: 14,
  },
  entryDesc: {
    fontSize: 10,
    color: '#334155',
    lineHeight: 1.5,
    marginTop: 3,
    marginBottom: 6,
  },
  metaRow: {
    fontSize: 9.5,
    lineHeight: 1.45,
    marginTop: 2,
    marginBottom: 3,
  },
  metaLabel: {
    fontWeight: 'bold',
    color: primaryColor,
  },
  metaVal: {
    color: '#475569',
  },
  impactBlock: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderLeftWidth: 2.5,
    borderLeftColor: primaryColor,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 3,
    marginTop: 6,
    marginBottom: 4,
  },
  impactLabel: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: primaryColor,
  },
  impactText: {
    flex: 1,
    fontSize: 9.5,
    color: '#0f172a',
    lineHeight: 1.45,
  },
});

export const DataDrivenPDF = ({ formData, sectionsEnabled = {}, primaryColor = null }) => {
  const p = formData?.personal || {};
  const styles = createStyles(primaryColor || '#0f172a');

  const contactItems = [
    { icon: IconEmail, text: p.email },
    { icon: IconPhone, text: p.phone },
    { icon: IconLocation, text: p.location },
    { icon: IconLinkedIn, text: p.linkedin ? formatContactLink(p.linkedin, 'linkedin') : null },
    { icon: IconGitHub, text: p.github ? formatContactLink(p.github, 'github') : null },
    { icon: IconLink, text: p.portfolio ? formatContactLink(p.portfolio, 'portfolio') : null },
  ].filter(i => i.text);

  const sk = formData?.technicalSkills || {};
  const tl = formData?.tools || {};

  const skillsBlocks = [
    { label: 'Core', items: formData?.coreCompetencies },
    { label: 'Testing', items: sk.testing },
    { label: 'Automation', items: sk.automation },
    { label: 'Programming', items: sk.programming },
    { label: 'Cloud/DB', items: [...(sk.cloud || []), ...(sk.databases || [])] },
    { label: 'Tools', items: [...(tl.testManagement || []), ...(tl.cicd || [])] }
  ].filter(b => b.items?.length > 0);

  const expEntries = (formData?.experience || []).filter(e => e.company?.trim());
  const eduEntries = (formData?.education || []).filter(e => e.degree?.trim());
  const certEntries = (formData?.certifications || []).filter(c => c.name?.trim());
  const projEntries = (formData?.projects || []).filter(e => e.title?.trim());

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        
        {/* HEADER BLOCK */}
        <View style={styles.header}>
          <Text style={styles.name}>{cleanStr(p.name || 'Your Name')}</Text>
          <Text style={styles.headline}>{cleanStr(p.jobTitle || 'QA Engineer')}</Text>
          {cleanStr(p?.targetHeadline) ? <Text style={styles.targetHeadline}>{cleanStr(p.targetHeadline)}</Text> : null}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {contactItems.map((item, i) => (
              <View key={i} style={styles.contactBlock}>
                <View style={styles.contactIconWrapper}>
                  <item.icon color="#94a3b8" />
                </View>
                <Text style={styles.contactItem}>{item.text}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* BODY */}
        <View style={styles.body}>
          
          {formData?.summary && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Summary</Text>
              <Text style={styles.text}>{cleanStr(formData.summary)}</Text>
            </View>
          )}

          {skillsBlocks.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Technical Expertise</Text>
              {skillsBlocks.map((block, i) => (
                <Text style={[styles.skText, { marginBottom: 4 }]} key={i}>
                  <Text style={styles.skLabel}>{block.label}: </Text>
                  <Text style={styles.skText}>{block.items.filter(Boolean).map(cleanStr).join(', ')}</Text>
                </Text>
              ))}
            </View>
          )}

          {expEntries.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Professional Experience</Text>
              {expEntries.map((e, i) => {
                const techClean = (e.technologies || '').trim();
                const achievements = e.achievements || [];
                return (
                  <View key={i} style={styles.entryBlock}>
                    <View wrap={false}>
                      <View style={styles.entryHeader}>
                        <Text style={styles.entryTitle}>{cleanStr(e.role)}</Text>
                        <Text style={styles.entryDate}>{cleanStr(e.duration)}</Text>
                      </View>
                      <Text style={styles.entrySub}>
                        {[e.company, e.location].filter(Boolean).map(cleanStr).join(' • ')}
                      </Text>
                      {techClean ? (
                        <Text style={styles.metaRow}>
                          <Text style={styles.metaLabel}>Technologies: </Text>
                          <Text style={styles.metaVal}>{cleanStr(techClean)}</Text>
                        </Text>
                      ) : null}
                      {e.impact ? (
                        <View style={styles.impactBlock} wrap={false}>
                          <Text style={styles.impactLabel}>Key Impact: </Text>
                          <Text style={styles.impactText}>{cleanStr(e.impact)}</Text>
                        </View>
                      ) : null}
                    </View>
                    {achievements.filter(Boolean).map((b, bi) => {
                      const txt = typeof b === 'object' ? b.text : b;
                      if (!txt?.trim()) return null;
                      return (
                        <View style={styles.bulletRow} key={bi}>
                          <View style={styles.bulletDot} />
                          <Text style={styles.bulletText}>{cleanStr(txt)}</Text>
                        </View>
                      );
                    })}
                  </View>
                );
              })}
            </View>
          )}

          {projEntries.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Projects</Text>
              {projEntries.map((p, i) => {
                const { cleanTitle, domainClean, techClean } = formatProjectMetadata(p);
                const respBullets = extractRespBullets(p).filter(b => cleanStr(b).length > 0);
                return (
                  <View key={i} style={styles.entryBlock}>
                    <View wrap={false}>
                      <View style={styles.entryHeader}>
                        <Text style={styles.entryTitle}>{cleanTitle}</Text>
                      </View>
                      {domainClean ? (
                        <Text style={styles.metaRow}>
                          <Text style={styles.metaLabel}>Domain: </Text>
                          <Text style={styles.metaVal}>{cleanStr(domainClean)}</Text>
                        </Text>
                      ) : null}
                      {techClean ? (
                        <Text style={styles.metaRow}>
                          <Text style={styles.metaLabel}>Technologies: </Text>
                          <Text style={styles.metaVal}>{cleanStr(techClean)}</Text>
                        </Text>
                      ) : null}
                    </View>
                    {p.description && (
                      <Text style={styles.entryDesc}>{cleanStr(p.description)}</Text>
                    )}
                    {respBullets.map((respTxt, rIdx) => (
                      <View key={rIdx} style={styles.bulletRow}>
                        <View style={styles.bulletDot} />
                        <Text style={styles.bulletText}>{cleanStr(respTxt)}</Text>
                      </View>
                    ))}
                    {p.impact ? (
                      <View style={styles.impactBlock}>
                        <Text style={styles.impactLabel}>Key Impact: </Text>
                        <Text style={styles.impactText}>{cleanStr(p.impact)}</Text>
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          )}

          {eduEntries.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Education</Text>
              {eduEntries.map((e, i) => (
                <View key={i} style={{ marginBottom: 12 }}>
                  <View style={styles.entryHeader} wrap={false}>
                    <Text style={styles.entryTitle}>{cleanStr(e.degree)}</Text>
                    <Text style={styles.entryDate}>{cleanStr(e.year)}</Text>
                  </View>
                  <Text style={styles.text}>{cleanStr(e.university)}</Text>
                </View>
              ))}
            </View>
          )}

          {certEntries.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Certifications</Text>
              {certEntries.map((c, i) => (
                <View key={i} style={{ marginBottom: 12 }}>
                  <View style={styles.entryHeader} wrap={false}>
                    <Text style={styles.entryTitle}>{cleanStr(c.name)}</Text>
                    <Text style={styles.entryDate}>{cleanStr(c.year)}</Text>
                  </View>
                  <Text style={styles.text}>{cleanStr(c.organization)}</Text>
                </View>
              ))}
            </View>
          )}

          {/* OPTIONAL SECTIONS */}
          {(formData?.awards || []).filter(a => a.title?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Awards & Recognition</Text>
              {(formData.awards || []).filter(a => a.title?.trim()).map((a, i) => (
                <View key={i} style={styles.entryBlock}>
                  <Text style={styles.entryTitle}>{cleanStr(a.title)}</Text>
                  <Text style={styles.entrySub}>{[a.organization, a.year].filter(Boolean).map(cleanStr).join(', ')}</Text>
                  {a.description && <Text style={styles.entryDesc}>{cleanStr(a.description)}</Text>}
                </View>
              ))}
            </View>
          )}

          {(formData?.volunteer || []).filter(v => v.organization?.trim() || v.role?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Volunteer Work</Text>
              {(formData.volunteer || []).filter(v => v.organization?.trim() || v.role?.trim()).map((v, i) => (
                <View key={i} style={styles.entryBlock}>
                  <Text style={styles.entryTitle}>{cleanStr(v.role || v.organization)}</Text>
                  <Text style={styles.entrySub}>{[v.organization, v.duration].filter(Boolean).map(cleanStr).join(' — ')}</Text>
                  {v.description && <Text style={styles.entryDesc}>{cleanStr(v.description)}</Text>}
                </View>
              ))}
            </View>
          )}

          {(formData?.publications || []).filter(p => p.title?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Publications & Talks</Text>
              {(formData.publications || []).filter(p => p.title?.trim()).map((p, i) => {
                const subParts = [p.publication, p.type, p.date].map(cleanStr).filter(v => v && v.length > 0);
                return (
                  <View key={i} style={styles.entryBlock} wrap={false}>
                    <View style={{ marginBottom: 2 }}>
                      <Text style={styles.entryTitle}>{cleanStr(p.title)}</Text>
                    </View>
                    {subParts.length > 0 && <Text style={styles.entrySub}>{subParts.join(' — ')}</Text>}
                    {p.url && (
                      <Text style={styles.metaRow}>
                        <Text style={styles.metaLabel}>URL: </Text>
                        <Text style={styles.metaVal}>{breakableUrlStr(p.url)}</Text>
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}

          {(formData?.openSource || []).filter(o => o.project?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Open Source Contributions</Text>
              {(formData.openSource || []).filter(o => o.project?.trim()).map((o, i) => {
                const subParts = [
                  cleanStr(o.role),
                  cleanStr(o.technologies ? `Tech: ${o.technologies}` : ''),
                  cleanStr(o.stars ? `★ ${o.stars}` : '')
                ].filter(v => v && v.length > 0);
                return (
                  <View key={i} style={styles.entryBlock} wrap={false}>
                    <View style={{ marginBottom: 2 }}>
                      <Text style={styles.entryTitle}>{cleanStr(o.project)}</Text>
                    </View>
                    {subParts.length > 0 && (
                      <Text style={styles.entrySub}>{subParts.join(' • ')}</Text>
                    )}
                    {o.description && <Text style={styles.entryDesc}>{cleanStr(o.description)}</Text>}
                    {o.url && (
                      <Text style={styles.metaRow}>
                        <Text style={styles.metaLabel}>Repository: </Text>
                        <Text style={styles.metaVal}>{breakableUrlStr(o.url)}</Text>
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}

          {(formData?.memberships || []).filter(m => m.organization?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Professional Memberships</Text>
              {(formData.memberships || []).filter(m => m.organization?.trim()).map((m, i) => {
                const subParts = [m.role, m.level, m.since ? `Since ${m.since}` : ''].map(cleanStr).filter(v => v && v.length > 0);
                return (
                  <View key={i} style={styles.entryBlock} wrap={false}>
                    <View style={{ marginBottom: 2 }}>
                      <Text style={styles.entryTitle}>{cleanStr(m.organization)}</Text>
                    </View>
                    {subParts.length > 0 && <Text style={styles.entrySub}>{subParts.join(' • ')}</Text>}
                  </View>
                );
              })}
            </View>
          )}

          {(formData?.portfolio || []).filter(p => p.title?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={45}>Portfolio & Case Studies</Text>
              {(formData.portfolio || []).filter(p => p.title?.trim()).map((p, i) => {
                const subParts = [p.type, p.technologies ? `Tech: ${p.technologies}` : ''].map(cleanStr).filter(v => v && v.length > 0);
                return (
                  <View key={i} style={styles.entryBlock} wrap={false}>
                    <View style={{ marginBottom: 2 }}>
                      <Text style={styles.entryTitle}>{cleanStr(p.title)}</Text>
                    </View>
                    {subParts.length > 0 && <Text style={styles.entrySub}>{subParts.join(' • ')}</Text>}
                    {p.description && <Text style={styles.entryDesc}>{cleanStr(p.description)}</Text>}
                    {p.url && (
                      <Text style={styles.metaRow}>
                        <Text style={styles.metaLabel}>Link: </Text>
                        <Text style={styles.metaVal}>{breakableUrlStr(p.url)}</Text>
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}

          {(formData?.patents || []).filter(p => p.title?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={15}>Patents & Innovations</Text>
              {(formData.patents || []).filter(p => p.title?.trim()).map((p, i) => {
                const subParts = [p.patentNumber ? `Patent #${p.patentNumber}` : '', p.status ? `Status: ${p.status}` : '', p.date].map(cleanStr).filter(v => v && v.length > 0);
                return (
                  <View key={i} style={styles.entryBlock} wrap={false}>
                    <View style={{ marginBottom: 2 }}>
                      <Text style={styles.entryTitle}>{cleanStr(p.title)}</Text>
                    </View>
                    {subParts.length > 0 && <Text style={styles.entrySub}>{subParts.join(' • ')}</Text>}
                    {p.description && <Text style={styles.entryDesc}>{cleanStr(p.description)}</Text>}
                  </View>
                );
              })}
            </View>
          )}

          {(formData?.languages || []).filter(l => l.language?.trim()).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle} minPresenceAhead={30}>Languages</Text>
              <Text style={styles.text}>
                {(formData.languages || []).filter(l => l.language?.trim()).map(l => `${cleanStr(l.language)} (${cleanStr(l.proficiency || 'Professional')})`).join(', ')}
              </Text>
            </View>
          )}

        </View>
      </Page>
    </Document>
  );
};
